import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { verifySession, login as apiLogin, logout as apiLogout, verifyMfaLogin } from '../api/client';

const AuthContext = createContext(null);
const TOKEN_KEYS = ['token', 'accessToken', 'idToken', 'jwt'];

const decodeJwtPayload = (token) => {
  if (!token || typeof token !== 'string') return null;

  const [, payload] = token.split('.');
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getTokenFromAuthResponse = (res) => {
  for (const key of TOKEN_KEYS) {
    if (res?.[key]) return res[key];
    if (res?.user?.[key]) return res.user[key];
  }
  return null;
};

const getRoleFromClaims = (claims) => {
  if (!claims) return null;
  if (claims.user?.role) return claims.user.role;
  if (claims.role) return claims.role;
  if (Array.isArray(claims.roles)) return claims.roles[0] || null;
  return null;
};

const normalizeAuthUser = (res) => {
  const token = getTokenFromAuthResponse(res);
  const claims = decodeJwtPayload(token);
  const responseUser = res?.user || {};

  return {
    ...responseUser,
    username: responseUser.username || res?.username || claims?.username || claims?.sub,
    role: getRoleFromClaims(claims) || responseUser.role || res?.role,
    roleDetails: responseUser.roleDetails || res?.roleDetails,
    claims
  };
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionMessage, setSessionMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await verifySession();
      if (res.success) setUser(normalizeAuthUser(res));
      else setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onExpired = (e) => {
      setUser(null);
      setSessionMessage(e?.detail?.message || 'Your session has expired. Please sign in again.');
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const clearSessionMessage = useCallback(() => setSessionMessage(''), []);

  const login = async (username, password) => {
    const res = await apiLogin(username, password);
    // res.mfaRequired === true → caller must collect a code and call verifyMfa.
    // We deliberately do NOT set the user here in that case (no session yet).
    if (res.success) {
      setUser(normalizeAuthUser(res));
      setSessionMessage('');
    }
    return res;
  };

  // Completes a login that required MFA. On success the backend has set the
  // auth cookies and returned the user payload.
  const verifyMfa = async (mfaToken, code, username) => {
    const res = await verifyMfaLogin(mfaToken, code, username);
    if (res.success) {
      setUser(normalizeAuthUser(res));
      setSessionMessage('');
    }
    return res;
  };

  const logout = async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      setSessionMessage('');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, logout, refresh, sessionMessage, clearSessionMessage }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
